<template>
  <div v-if="importData">
    <div class="d-flex align-center mb-3">
      <h3 class="input-text-size">Validation Results</h3>
      <v-spacer />
      <v-btn
        text
        small
        @click="$emit('close')"
      >
        Close
      </v-btn>
    </div>

    <!-- Full File Reports -->
    <div class="d-flex mb-3" style="gap: 8px;">
      <v-btn
        small
        outlined
        color="primary"
        @click="downloadFullFileErrorReport"
      >
        Download Error Report (CSV)
      </v-btn>
      <v-btn
        small
        outlined
        color="primary"
        @click="downloadFullFileErrorReportPdf"
      >
        Download Error Report (PDF)
      </v-btn>
      <v-btn
        small
        outlined
        color="primary"
        @click="downloadFullFileAllRecords"
      >
        Download All Records
      </v-btn>
      <v-btn
        small
        outlined
        color="primary"
        @click="printAllRecordsPdf"
      >
        Print All Records (PDF)
      </v-btn>
    </div>

    <!-- Batch Tabs -->
    <v-card outlined class="mb-3">
      <v-tabs
        v-model="selectedBatchIndex"
        show-arrows
        dense
        height="40"
      >
        <v-tab
          v-for="batch in batches"
          :key="batch.batchNumber"
        >
          Batch {{ batch.batchNumber }}
          <v-chip
            x-small
            class="ml-2"
            :color="getBatchStatusColor(batch)"
          >
            {{ batch.totalRecords }}
          </v-chip>
        </v-tab>
      </v-tabs>
    </v-card>

    <!-- Side-by-side layout: Table on left, Summary on right (wraps on small screens) -->
    <div class="d-flex flex-wrap" style="gap: 16px;">
      <!-- Left side: Table and filters -->
      <div style="flex: 1; min-width: 500px;">
        <!-- Filter Checkboxes -->
        <v-card outlined class="mb-3">
          <v-card-text class="py-2">
            <div class="d-flex align-center flex-wrap" style="gap: 8px;">
              <v-chip-group
                v-model="activeStatusFilters"
                multiple
                active-class="primary--text"
              >
                <v-chip
                  filter
                  outlined
                  small
                  :color="statusChipColor('Valid')"
                >
                  Valid
                </v-chip>
                <v-chip
                  filter
                  outlined
                  small
                  :color="statusChipColor('Not Found')"
                >
                  Not Found
                </v-chip>
                <v-chip
                  filter
                  outlined
                  small
                  :color="statusChipColor('Overpayment')"
                >
                  Overpayment
                </v-chip>
                <v-chip
                  filter
                  outlined
                  small
                  :color="statusChipColor('Underpayment')"
                >
                  Underpayment
                </v-chip>
                <v-chip
                  filter
                  outlined
                  small
                  :color="statusChipColor('Duplicate')"
                >
                  Duplicate
                </v-chip>
                <v-chip
                  filter
                  outlined
                  small
                  :color="statusChipColor('Pending Payment')"
                >
                  Pending Payment
                </v-chip>
                <v-chip
                  filter
                  outlined
                  small
                  :color="statusChipColor('Over/Under')"
                >
                  Over/Under
                </v-chip>
                <v-chip
                  filter
                  outlined
                  small
                  :color="statusChipColor('Missing Fees')"
                >
                  Missing Fees
                </v-chip>
              </v-chip-group>
              <v-divider vertical class="mx-1" />
              <v-chip-group
                v-model="activeMonthFilters"
                multiple
                active-class="primary--text"
              >
                <v-chip
                  filter
                  outlined
                  small
                >
                  Current Month
                </v-chip>
                <v-chip
                  filter
                  outlined
                  small
                >
                  Prev Month
                </v-chip>
              </v-chip-group>
            </div>
          </v-card-text>
        </v-card>

        <!-- Search box -->
        <v-text-field
          v-model="searchText"
          label="Search by Tax ID"
          outlined
          dense
          clearable
          class="mb-3"
        />

        <!-- Records Table -->
        <v-data-table
          :headers="headers"
          :items="filteredRecords"
          :search="searchText"
          :custom-filter="searchByTaxId"
          :loading="loading"
          dense
          class="elevation-1"
        >
          <template #[`item.account`]="{ item }">
            <div class="text-no-wrap">
              <strong>{{ item.taxYear }}</strong> - {{ item.taxId }}
            </div>
            <div class="text-caption text--secondary text-truncate" style="max-width: 130px;">
              {{ item.propertyOrParcelId }}
            </div>
          </template>
          <template #[`item.validationStatus`]="{ item }">
            <v-chip
              :color="getStatusColor(item.validationStatus)"
              x-small
              dark
            >
              {{ item.validationStatus }}
            </v-chip>
            <v-chip
              v-if="item.hasOverUnderAdjustment"
              x-small
              color="amber darken-1"
              dark
              class="ml-1"
            >
              O/U
            </v-chip>
            <v-tooltip v-if="item.isMissingFees" bottom>
              <template #activator="{ on, attrs }">
                <v-chip
                  x-small
                  color="brown"
                  dark
                  class="ml-1"
                  v-bind="attrs"
                  v-on="on"
                >
                  Missing Fees
                </v-chip>
              </template>
              <span>Bank file shows $0 fee but system has expected fee &mdash; fee will not be paid.</span>
            </v-tooltip>
            <v-tooltip v-if="item.usedPrevMonthPenalty" bottom>
              <template #activator="{ on, attrs }">
                <v-chip
                  x-small
                  color="deep-purple"
                  dark
                  class="ml-1"
                  v-bind="attrs"
                  v-on="on"
                >
                  Prev Mo
                </v-chip>
              </template>
              <span>Payment matched previous month's penalty</span>
            </v-tooltip>
            <v-tooltip v-if="item.isAdjusted || item.isApproved" bottom>
              <template #activator="{ on, attrs }">
                <v-chip
                  x-small
                  :color="item.isApproved ? 'green' : 'blue'"
                  dark
                  class="ml-1"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ item.isApproved ? 'OK' : 'Adjusted' }}
                </v-chip>
              </template>
              <div v-if="item.isApproved">
                <strong>Approved</strong> by {{ item.approvedBy || 'Unknown' }}<br />
                {{ formatAdjustedDate(item.approvedAt) }}
              </div>
              <div v-if="item.isAdjusted" :class="{ 'mt-2': item.isApproved }">
                <strong>Adjusted</strong> by {{ item.adjustedBy || 'Unknown' }}<br />
                {{ formatAdjustedDate(item.adjustedAt) }}
              </div>
            </v-tooltip>
            <v-btn
              v-if="item.validationStatus === 'DUPLICATE'"
              x-small
              color="warning"
              class="ml-1"
              :loading="isRemovingDuplicate(item)"
              @click="handleRemoveDuplicate(item)"
            >
              Clear
            </v-btn>
            <v-btn
              x-small
              text
              color="primary"
              class="ml-1"
              @click="openEditDialog(item)"
            >
              Edit
            </v-btn>
          </template>
          <template #[`item.taxPayment`]="{ item }">
            ${{ formatCurrency(item.taxPayment) }}
          </template>
          <template #[`item.expectedTax`]="{ item }">
            ${{ formatCurrency(item.expectedTax) }}
          </template>
          <template #[`item.penaltyPayment`]="{ item }">
            ${{ formatCurrency(item.penaltyPayment) }}
          </template>
          <template #[`item.expectedPenalty`]="{ item }">
            ${{ formatCurrency(item.expectedPenalty) }}
          </template>
          <template #[`item.expectedPenaltyPrevMonth`]="{ item }">
            ${{ formatCurrency(item.expectedPenaltyPrevMonth) }}
          </template>
          <template #[`item.feePayment`]="{ item }">
            ${{ formatCurrency(item.feePayment) }}
          </template>
          <template #[`item.expectedFee`]="{ item }">
            ${{ formatCurrency(item.expectedFee) }}
          </template>
          <template #[`item.overpaymentAmount`]="{ item }">
            <span :class="{ 'green--text': item.overpaymentAmount > 0 }">
              ${{ formatCurrency(item.overpaymentAmount) }}
            </span>
          </template>
          <template #[`item.paidDate`]="{ item }">
            {{ formatPaidDate(item.paidDate) }}
          </template>
          <template #[`item.totalPayment`]="{ item }">
            ${{ formatCurrency(getTotalPayment(item)) }}
          </template>
          <template #[`item.expectedAmount`]="{ item }">
            ${{ formatCurrency(item.expectedAmount) }}
          </template>
          <template #[`item.amountDifference`]="{ item }">
            <span
              :class="getDifferenceClass(item.amountDifference)"
            >
              {{ item.amountDifference > 0 ? '+' : '' }}${{ formatCurrency(item.amountDifference) }}
            </span>
          </template>
        </v-data-table>

        <!-- Batch Download Buttons -->
        <div class="d-flex mt-3" style="gap: 8px;">
          <v-btn
            small
            outlined
            color="primary"
            @click="downloadErrorReport"
          >
            Batch Error Report (CSV)
          </v-btn>
          <v-btn
            small
            outlined
            color="primary"
            @click="downloadErrorReportPdf"
          >
            Batch Error Report (PDF)
          </v-btn>
          <v-btn
            small
            outlined
            color="primary"
            @click="downloadAllRecords"
          >
            Batch All Records
          </v-btn>
        </div>
      </div>

      <!-- Right side: Summary Panel (compact) -->
      <div class="summary-panel">
        <v-card outlined>
          <v-card-title class="text-caption py-1 px-2">
            Batch {{ selectedBatch ? selectedBatch.batchNumber : '' }} Summary
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-2">
            <div class="summary-row">
              <span class="summary-label">Records:</span>
              <span class="summary-value">{{ summary.totalRecords }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Submitted:</span>
              <span class="summary-value">${{ formatCurrency(summary.totalAmount) }}</span>
            </div>
            <v-divider class="my-1" />
            <div class="summary-row">
              <span class="summary-label success--text">Valid:</span>
              <span class="summary-value success--text">
                {{ summary.validCount }} / ${{ formatCurrency(summary.validAmount) }}
              </span>
            </div>
            <div class="summary-row">
              <span class="summary-label grey--text">Not Found:</span>
              <span class="summary-value grey--text">
                {{ summary.notFoundCount }} / ${{ formatCurrency(summary.notFoundAmount) }}
              </span>
            </div>
            <div class="summary-row">
              <span class="summary-label orange--text">Overpayment:</span>
              <span class="summary-value orange--text">
                {{ summary.overpaymentCount }} / +${{ formatCurrency(summary.overpaymentSum) }}
              </span>
            </div>
            <div class="summary-row">
              <span class="summary-label red--text">Underpayment:</span>
              <span class="summary-value red--text">
                {{ summary.underpaymentCount }} / -${{ formatCurrency(summary.underpaymentSum) }}
              </span>
            </div>
            <div class="summary-row">
              <span class="summary-label purple--text">Duplicate:</span>
              <span class="summary-value purple--text">
                {{ summary.duplicateCount }} / ${{ formatCurrency(summary.duplicateAmount) }}
              </span>
            </div>
            <div class="summary-row">
              <span class="summary-label blue--text">Pending Pmt:</span>
              <span class="summary-value blue--text">
                {{ summary.pendingExistsCount }} /
                ${{ formatCurrency(summary.pendingExistsAmount) }}
              </span>
            </div>
            <div class="summary-row">
              <span class="summary-label amber--text text--darken-1">Over/Under:</span>
              <span class="summary-value amber--text text--darken-1">
                {{ summary.overUnderCount }} /
                ${{ formatCurrency(summary.overUnderAmount) }}
              </span>
            </div>
            <v-divider class="my-1" />
            <div class="summary-row font-weight-bold">
              <span class="summary-label">Refund:</span>
              <span class="summary-value">${{ formatCurrency(summary.refundAmount) }}</span>
            </div>
            <div class="summary-row font-weight-bold">
              <span class="summary-label">Paid/Wire:</span>
              <span class="summary-value">${{ formatCurrency(summary.paidAmount) }}</span>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </div>

    <!-- Payment Footer (Full Width Below Two-Column Layout) -->
    <v-card outlined class="mt-3">
      <v-card-title class="text-subtitle-1 py-2">
        Payment for Batch {{ selectedBatch ? selectedBatch.batchNumber : '' }}
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-3">
        <div class="d-flex flex-wrap" style="gap: 16px;">
          <!-- Left Column: Payment Options or Payment Created Status -->
          <div style="flex: 1; min-width: 200px;">
            <template v-if="!batchPaymentCreated">
              <div class="text-caption font-weight-bold mb-2">
                Payment Options
              </div>
              <v-checkbox
                v-model="postOverPayments"
                label="Post Over Payments"
                dense
                hide-details
                class="mt-0"
              />
              <v-checkbox
                v-model="postPartials"
                label="Post Partials"
                dense
                hide-details
                class="mt-0"
              />
              <v-checkbox
                v-model="postDuplicates"
                label="Post Duplicates"
                dense
                hide-details
                class="mt-0"
              />
            </template>
            <template v-else>
              <div class="text-h6 success--text mb-2">
                Payment Created
              </div>
              <div class="text-caption mb-1">
                <strong>Payment Number:</strong> {{ createdPaymentNumber || 'N/A' }}
              </div>
              <div class="text-caption mb-2">
                <strong>Fiscal Year:</strong> {{ createdPaymentFiscalYear || 'N/A' }}
              </div>
              <v-btn
                small
                color="primary"
                :disabled="!createdPaymentNumber"
                @click="handleViewPayment"
              >
                View Payment
              </v-btn>
            </template>
          </div>

          <!-- Middle Column: Payment Type and Details -->
          <div style="flex: 1; min-width: 250px;">
            <template v-if="!batchPaymentCreated">
              <v-row dense>
                <v-col cols="6">
                  <v-select
                    v-model="selectedPaymentType"
                    :items="paymentTypeOptions"
                    item-text="text"
                    item-value="value"
                    label="Payment Type"
                    dense
                    outlined
                    hide-details
                    class="mb-2"
                  />
                </v-col>
                <v-col cols="6">
                  <!-- Conditional field based on payment type -->
                  <v-text-field
                    v-if="selectedPaymentType === 'CHECK'"
                    v-model="checkNumber"
                    label="Check Number"
                    dense
                    outlined
                    hide-details
                    class="mb-2"
                  />
                  <v-select
                    v-else-if="selectedPaymentType === 'EFT'"
                    v-model="eftType"
                    :items="eftTypeOptions"
                    item-text="text"
                    item-value="value"
                    label="EFT Type"
                    dense
                    outlined
                    hide-details
                    class="mb-2"
                  />
                  <v-select
                    v-else-if="selectedPaymentType === 'CREDIT'"
                    v-model="creditType"
                    :items="creditTypeOptions"
                    item-text="text"
                    item-value="value"
                    label="Card Type"
                    dense
                    outlined
                    hide-details
                    class="mb-2"
                  />
                </v-col>
              </v-row>
              <v-text-field
                v-model="paidBy"
                label="Paid By"
                dense
                outlined
                hide-details
                class="mb-2"
              />
              <v-row dense>
                <v-col cols="6">
                  <v-text-field
                    v-model="postingDate"
                    label="Posting Date"
                    dense
                    outlined
                    readonly
                    hide-details
                    class="mb-2"
                  />
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-model="selectedWorkstation"
                    :items="paymentWkstNumList"
                    label="Workstation"
                    dense
                    outlined
                    hide-details
                    class="mb-2"
                  />
                </v-col>
              </v-row>
              <v-btn
                small
                color="primary"
                :disabled="!canCreatePayment || !postingDate
                  || !selectedWorkstation || creatingPayment || creatingPaymentAll"
                :loading="creatingPayment"
                @click="handleCreatePayment"
              >
                Create Payment for Batch
                {{ selectedBatch ? selectedBatch.batchNumber : '' }}
              </v-btn>

              <v-btn
                v-if="batches.length > 1 && unprocessedBatchCount > 0"
                small
                color="success"
                class="ml-2"
                :disabled="allBatchesUnapprovedOverpaymentCount > 0
                  || !postingDate
                  || !selectedWorkstation
                  || creatingPayment || creatingPaymentAll"
                :loading="creatingPaymentAll"
                @click="handleCreatePaymentAll"
              >
                Create Payment for All Batches
                ({{ unprocessedBatchCount }})
              </v-btn>

              <!-- Warning for unapproved overpayments -->
              <v-alert
                v-if="unapprovedOverpaymentCount > 0"
                type="warning"
                dense
                text
                class="mt-2 mb-0"
              >
                <div class="text-caption">
                  {{ unapprovedOverpaymentCount }} overpayment record(s)
                  need review before payment can be created.
                </div>
              </v-alert>

              <!-- Loading/Status Messages -->
              <div v-if="statusMessage" class="mt-3 text-caption">
                {{ statusMessage }}
              </div>
            </template>
          </div>

          <!-- Right Column: Placeholder or additional info -->
          <div style="flex: 1; min-width: 200px;">
            <div class="text-caption">
              <strong>File:</strong> {{ importData.fileName || 'N/A' }}
            </div>
            <div class="text-caption">
              <strong>Total Batches:</strong> {{ batches.length }}
            </div>
            <div class="text-caption">
              <strong>Total Records:</strong> {{ importData.totalRecords || 0 }}
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Payment Dialog -->
    <CRUDDialog
      ref="paymentCrudDialog"
      :baseCrudKey="paymentDialogBaseCrudKey"
      :canSave="false"
      @upsertItem="onSavePayment"
    >
      <template #editedItem>
        <paymentDialog
          ref="paymentDialogRef"
          :baseCrudKey="paymentDialogBaseCrudKey"
          @callFromPayDialog="onSavePayment"
        />
      </template>
      <template #additionalActions>
        <UploadFile
          class="mtgtrans uploadFile"
          text
          :bucketName="attachmentConstants.bucketName"
          :program="attachmentConstants.program"
          :parentObjectType="attachmentConstants.parentObjectType"
          :parentObjectId="createdPaymentId || ''"
          :itemIsSaved="!!createdPaymentId"
        >
          <template v-slot:activator="{ activatorProps }">
            <v-btn
              :disabled="!createdPaymentId"
              class="input-text-size payment-transaction-list-upload-btn"
              id="paymentTransactionListUploadBtn"
              text
              color="blue darken-1"
              @click="activatorProps.leftClick"
            >
              {{ activatorProps.caption }}
            </v-btn>
          </template>
        </UploadFile>
        <AuditDialog
          v-if="createdPaymentId"
          :identifier="createdPaymentId"
          activatorCaption="Audit History"
          activatorCaptionStyle="input-text-size"
        />
      </template>
      <template #additionalActionsAfterSave>
        <v-btn
          class="input-text-size payment-transaction-list-save-btn"
          id="paymentTransactionListSaveBtn"
          text
          color="blue darken-1"
          @click="onSavePayment(false)"
        >
          Save
        </v-btn>
        <v-btn
          class="input-text-size payment-transaction-list-save-print-btn"
          id="paymentTransactionListSavePrintBtn"
          text
          color="blue darken-1"
          @click="onSavePayment(true)"
        >
          Save & print
        </v-btn>
      </template>
    </CRUDDialog>

    <!-- Edit Payment Modal -->
    <v-dialog
      v-model="editDialog"
      max-width="900px"
      persistent
    >
      <v-card>
        <v-card-title class="text-subtitle-1">
          Edit Record
        </v-card-title>
        <v-divider />
        <v-card-text v-if="editingRecord" class="pt-4">
          <!-- Two-column layout -->
          <div class="d-flex" style="gap: 24px;">
            <!-- Left Column: Tax Reference / Rematch -->
            <div style="flex: 0 0 300px;">
              <div class="text-subtitle-2 mb-2">Tax Reference</div>

              <!-- Rematch history alert -->
              <v-alert
                v-if="editingRecord.isRematched"
                type="info"
                dense
                text
                class="mb-3"
              >
                <div class="text-caption">
                  <strong>Rematched</strong> by {{ editingRecord.rematchedBy }}
                  on {{ formatAdjustedDate(editingRecord.rematchedAt) }}
                </div>
                <div class="text-caption mt-1">
                  <strong>Original from file:</strong>
                  Year: {{ editingRecord.originalTaxYear }} |
                  Tax ID: {{ editingRecord.originalTaxId }}
                </div>
              </v-alert>

              <!-- Tax Year and Tax ID fields -->
              <v-row dense>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="editTaxYear"
                    label="Tax Year"
                    type="number"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="editTaxId"
                    label="Tax ID"
                    type="number"
                    outlined
                    dense
                  />
                </v-col>
              </v-row>

              <!-- Rematch Button - shown when tax year or tax id has changed -->
              <v-btn
                v-if="taxReferenceChanged"
                small
                color="primary"
                :disabled="!canRematch || rematching"
                :loading="rematching"
                class="mb-3"
                @click="handleRematch"
              >
                Rematch Record
              </v-btn>

              <!-- Property/Parcel ID (read-only) -->
              <v-text-field
                :value="editingRecord.propertyOrParcelId || 'N/A'"
                label="Property/Parcel ID"
                readonly
                outlined
                dense
                tabindex="-1"
              />

              <!-- Status display -->
              <div class="mb-3">
                <v-chip
                  :color="getStatusColor(editingRecord.validationStatus)"
                  x-small
                >
                  {{ editingRecord.validationStatus }}
                </v-chip>
                <v-chip
                  v-if="editingRecord.hasOverUnderAdjustment"
                  x-small
                  color="amber darken-1"
                  dark
                  class="ml-1"
                >
                  O/U {{ editingRecord.amountDifference > 0 ? '+' : '' }}${{
                    formatCurrency(editingRecord.amountDifference)
                  }}
                </v-chip>
                <v-chip
                  v-if="editingRecord.usedPrevMonthPenalty"
                  x-small
                  color="deep-purple"
                  dark
                  class="ml-1"
                >
                  Prev Mo
                </v-chip>
                <v-chip
                  v-if="editingRecord.isMissingFees"
                  x-small
                  color="brown"
                  dark
                  class="ml-1"
                >
                  Missing Fees
                </v-chip>
              </div>
              <!-- Missing-fees explanation: clears when user enters a non-zero fee. -->
              <v-alert
                v-if="editingRecord.isMissingFees"
                type="warning"
                dense
                text
                class="mb-3"
              >
                <div class="text-caption">
                  Bank file shows <strong>$0</strong> in fees but the system has an expected fee of
                  <strong>${{ formatCurrency(editingRecord.expectedFee) }}</strong>.
                  Applying payments will post <strong>$0</strong> to the fee &mdash; the
                  treasurer can later remove the system fee. Entering a non-zero fee
                  amount below will override this and pay the fee normally.
                </div>
              </v-alert>

              <!-- Error description if any -->
              <div
                v-if="editingRecord.errorDescription"
                class="text-caption red--text mb-3"
              >
                {{ editingRecord.errorDescription }}
              </div>

              <!-- Adjustment history -->
              <v-alert
                v-if="editingRecord.isAdjusted"
                type="info"
                dense
                text
                class="mb-0"
              >
                <div class="text-caption">
                  <strong>Amounts adjusted</strong> by {{ editingRecord.adjustedBy }}
                  on {{ formatAdjustedDate(editingRecord.adjustedAt) }}
                </div>
                <div class="text-caption mt-1">
                  <strong>Original:</strong>
                  Tax: ${{ formatCurrency(editingRecord.originalTaxPayment) }} |
                  Pen: ${{ formatCurrency(editingRecord.originalPenaltyPayment) }} |
                  Fee: ${{ formatCurrency(editingRecord.originalFeePayment) }}
                </div>
              </v-alert>
            </div>

            <!-- Vertical divider -->
            <v-divider vertical />

            <!-- Right Column: Payment Amounts -->
            <div style="flex: 1;">
              <div class="text-subtitle-2 mb-2">Payment Amounts</div>

              <!-- Payment fields in 2 columns: Actual vs Expected -->
              <v-row dense>
                <v-col cols="6">
                  <v-text-field
                    v-model="editTaxPaymentStr"
                    label="Tax Payment"
                    prefix="$"
                    type="text"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    :value="formatCurrency(editingRecord.expectedTax)"
                    label="Expected Tax"
                    prefix="$"
                    readonly
                    outlined
                    dense
                    tabindex="-1"
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="6">
                  <v-text-field
                    v-model="editPenaltyPaymentStr"
                    label="Penalty Payment"
                    prefix="$"
                    type="text"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    :value="formatCurrency(editingRecord.expectedPenalty)"
                    label="Expected Penalty"
                    prefix="$"
                    readonly
                    outlined
                    dense
                    tabindex="-1"
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="6">
                  <v-text-field
                    v-model="editFeePaymentStr"
                    label="Fee Payment"
                    prefix="$"
                    type="text"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    :value="formatCurrency(editingRecord.expectedFee)"
                    label="Expected Fee"
                    prefix="$"
                    readonly
                    outlined
                    dense
                    tabindex="-1"
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="6">
                  <v-text-field
                    :value="formatSignedAmount(editOverpaymentAmount)"
                    label="Overpayment (auto)"
                    prefix="$"
                    readonly
                    outlined
                    dense
                    tabindex="-1"
                    hint="Auto-balances to original"
                    persistent-hint
                  />
                </v-col>
                <v-col cols="6" />
              </v-row>

              <v-divider class="my-3" />

              <!-- Totals -->
              <v-row dense>
                <v-col cols="6">
                  <v-text-field
                    :value="formatCurrency(editNewTotal)"
                    label="New Total"
                    prefix="$"
                    readonly
                    outlined
                    dense
                    :error="!editTotalMatches"
                    :error-messages="editTotalMatches ? '' : 'Must match original'"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    :value="formatCurrency(editOriginalTotal)"
                    label="Original Total"
                    prefix="$"
                    readonly
                    outlined
                    dense
                    tabindex="-1"
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="6">
                  <v-text-field
                    :value="formatCurrency(editingRecord.expectedAmount)"
                    label="Expected Total"
                    prefix="$"
                    readonly
                    outlined
                    dense
                    tabindex="-1"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    :value="formatCurrency(editingRecord.expectedAmountPrevMonth)"
                    label="Prev Month Exp"
                    prefix="$"
                    readonly
                    outlined
                    dense
                    tabindex="-1"
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="6" />
                <v-col cols="6">
                  <v-text-field
                    :value="formatCurrency(editingRecord.expectedPenaltyPrevMonth)"
                    label="Prev Month Pen"
                    prefix="$"
                    readonly
                    outlined
                    dense
                    tabindex="-1"
                  />
                </v-col>
              </v-row>
            </div>
          </div>

          <v-divider class="my-3" />

          <!-- Approval Checkbox -->
          <v-checkbox
            v-model="editApproved"
            label="Mark as reviewed/approved (OK)"
            dense
            hide-details
            class="mt-0"
          />
          <div v-if="editingRecord.isApproved" class="text-caption grey--text mt-1">
            Previously approved by {{ editingRecord.approvedBy }}
            on {{ formatAdjustedDate(editingRecord.approvedAt) }}
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="closeEditDialog"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!editTotalMatches || savingEdit"
            :loading="savingEdit"
            @click="saveEditPayments"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  mapActions, mapState, mapMutations, mapGetters,
} from 'vuex';
import CRUDDialog from '../../common/base/crud-2.0/CRUDDialog.vue';
import paymentDialog from '../payments/paymenteditDialog/paymentDialog.vue';
import UploadFile from '../../common/UploadFile.vue';
import AuditDialog from '../../common/audit/AuditDialog.vue';
import { clients } from '../../../util/clients';
import { generateQueryString } from '../../common/base/report/report-util';

export default {
  name: 'LockboxRecordsTable',
  components: {
    CRUDDialog,
    paymentDialog,
    UploadFile,
    AuditDialog,
  },
  props: {
    importData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      selectedBatchIndex: 0,
      // Status filter chips: indices into statusFilterLabels
      // Default: all selected (0-7)
      activeStatusFilters: [0, 1, 2, 3, 4, 5, 6, 7],
      statusFilterLabels: [
        'Valid', 'Not Found', 'Overpayment', 'Underpayment',
        'Duplicate', 'Pending Payment', 'Over/Under', 'Missing Fees',
      ],
      // Month filter chips: 0=Current, 1=Prev. Default: both selected
      activeMonthFilters: [0, 1],
      searchText: '',
      // Payment creation fields
      postOverPayments: true,
      postPartials: true,
      postDuplicates: false,
      selectedWorkstation: null,
      // Tender type fields
      selectedPaymentType: 'EFT',
      checkNumber: '',
      eftType: 'OTHER',
      creditType: '',
      paidBy: '',
      paymentTypeOptions: [
        { value: 'EFT', text: 'EFT' },
        { value: 'CHECK', text: 'Check' },
        { value: 'CASH', text: 'Cash' },
        { value: 'CREDIT', text: 'Credit Card' },
        { value: 'OTHER', text: 'Other' },
      ],
      eftTypeOptions: [
        { value: 'SIMPLIFILE', text: 'Simplifile' },
        { value: 'OTC', text: 'OTC' },
        { value: 'IRS', text: 'IRS' },
        { value: 'FORTE', text: 'Forte' },
        { value: 'CSC', text: 'CSC' },
        { value: 'OTHER', text: 'Other' },
      ],
      creditTypeOptions: [
        { value: 'VISA', text: 'Visa' },
        { value: 'MC', text: 'MasterCard' },
        { value: 'AMEX', text: 'American Express' },
        { value: 'DISC', text: 'Discover' },
        { value: 'OTHER', text: 'Other' },
      ],
      creatingPayment: false,
      creatingPaymentAll: false,
      statusMessage: '',
      createdPaymentId: null,
      createdPaymentNumber: null,
      createdPaymentFiscalYear: null,
      pollingInterval: null,
      // Track payment status per batch
      batchPayments: {},
      // Remove duplicate tracking
      removingDuplicateIndex: null,
      // Payment dialog
      paymentDialogBaseCrudKey: 'lockboxPaymentDialog',
      // Attachment constants for upload file
      attachmentConstants: {
        bucketName: 'taxPayment',
        program: 'taxPayment',
        parentObjectType: 'taxPayments',
      },
      headers: [
        {
          text: 'Account', value: 'account', width: '140px', sortable: false,
        },
        {
          text: 'Tax Pmt', value: 'taxPayment', width: '85px', sortable: true,
        },
        {
          text: 'Exp Tax', value: 'expectedTax', width: '85px', sortable: true,
        },
        {
          text: 'Pen Pmt', value: 'penaltyPayment', width: '80px', sortable: true,
        },
        {
          text: 'Exp Pen', value: 'expectedPenalty', width: '80px', sortable: true,
        },
        {
          text: 'Prev Pen', value: 'expectedPenaltyPrevMonth', width: '80px', sortable: true,
        },
        {
          text: 'Fee Pmt', value: 'feePayment', width: '75px', sortable: true,
        },
        {
          text: 'Exp Fee', value: 'expectedFee', width: '75px', sortable: true,
        },
        {
          text: 'Overpmt', value: 'overpaymentAmount', width: '80px', sortable: true,
        },
        {
          text: 'Paid Date', value: 'paidDate', width: '90px', sortable: true,
        },
        {
          text: 'Total Pmt', value: 'totalPayment', width: '85px', sortable: true,
        },
        {
          text: 'Expected', value: 'expectedAmount', width: '85px', sortable: true,
        },
        {
          text: 'Difference', value: 'amountDifference', width: '85px', sortable: true,
        },
        {
          text: 'Status', value: 'validationStatus', width: '220px', sortable: true,
        },
      ],
      // Edit payment modal
      editDialog: false,
      editingRecord: null,
      editingRecordIndex: null,
      // Store as formatted strings to preserve 2 decimal places
      editTaxPaymentStr: '0.00',
      editPenaltyPaymentStr: '0.00',
      editFeePaymentStr: '0.00',
      editOverpaymentAmountStr: '0.00',
      savingEdit: false,
      editApproved: false,
      // Rematch fields
      editTaxYear: null,
      editTaxId: null,
      rematching: false,
    };
  },
  computed: {
    ...mapState({
      paymentWkstNumList: (state) => state.tax.taxPayment.paymentWkstNumList || [],
    }),
    ...mapGetters('base/crud', ['item']),
    batches() {
      if (!this.importData || !this.importData.batches) {
        return [];
      }
      return this.importData.batches;
    },
    selectedBatch() {
      if (this.batches.length === 0) return null;
      return this.batches[this.selectedBatchIndex] || this.batches[0];
    },
    batchPaymentCreated() {
      if (!this.selectedBatch) return false;
      const { batchNumber } = this.selectedBatch;
      return !!(this.batchPayments[batchNumber] && this.batchPayments[batchNumber].paymentCreated);
    },
    postingDate() {
      // Get posting date from importData
      if (this.importData && this.importData.postingDate) {
        const date = new Date(this.importData.postingDate);
        return date.toLocaleDateString();
      }
      return '';
    },
    defaultPaidBy() {
      // Default paid by value for lockbox batches
      if (this.selectedBatch) {
        return `Lockbox Batch ${this.selectedBatch.batchNumber}`;
      }
      return 'Lockbox Batch';
    },
    unapprovedOverpaymentCount() {
      // Count overpayment records that haven't been reviewed (approved or adjusted)
      return this.batchRecords.filter(
        (r) => r.validationStatus === 'OVERPAYMENT' && !r.isApproved && !r.isAdjusted,
      ).length;
    },
    canCreatePayment() {
      // Can only create payment if all overpayments have been reviewed
      return this.unapprovedOverpaymentCount === 0;
    },
    unprocessedBatchCount() {
      if (!this.batches || this.batches.length === 0) return 0;
      return this.batches.filter((b) => !b.paymentApplied).length;
    },
    allBatchesUnapprovedOverpaymentCount() {
      if (!this.importData || !this.importData.records) return 0;
      return this.importData.records.filter(
        (r) => r.validationStatus === 'OVERPAYMENT' && !r.isApproved && !r.isAdjusted,
      ).length;
    },
    isNotFoundRecord() {
      // Check if current editing record is NOT_FOUND status
      return this.editingRecord && this.editingRecord.validationStatus === 'NOT_FOUND';
    },
    canRematch() {
      // Can rematch if tax year and tax id have values
      return this.editTaxYear && this.editTaxId;
    },
    taxReferenceChanged() {
      // Check if tax year or tax id has been changed from the record's current values
      if (!this.editingRecord) return false;
      return this.editTaxYear !== this.editingRecord.taxYear
        || this.editTaxId !== this.editingRecord.taxId;
    },
    batchRecords() {
      // Get records for the selected batch
      if (!this.importData || !this.importData.records || !this.selectedBatch) {
        return [];
      }
      const { batchNumber } = this.selectedBatch;
      return this.importData.records.filter((record) => record.batchNumber === batchNumber);
    },
    showValid() { return this.activeStatusFilters.includes(0); },
    showNotFound() { return this.activeStatusFilters.includes(1); },
    showOverpayment() { return this.activeStatusFilters.includes(2); },
    showUnderpayment() { return this.activeStatusFilters.includes(3); },
    showDuplicate() { return this.activeStatusFilters.includes(4); },
    showPendingExists() { return this.activeStatusFilters.includes(5); },
    showOverUnder() { return this.activeStatusFilters.includes(6); },
    showMissingFees() { return this.activeStatusFilters.includes(7); },
    showCurrentMonth() { return this.activeMonthFilters.includes(0); },
    showPrevMonth() { return this.activeMonthFilters.includes(1); },
    filteredRecords() {
      return this.batchRecords.filter((record) => {
        // Month filter
        if (record.usedPrevMonthPenalty && !this.showPrevMonth) return false;
        if (!record.usedPrevMonthPenalty && !this.showCurrentMonth) return false;

        // Missing-fees acts as an additive filter: a record with isMissingFees is
        // hidden when the Missing Fees chip is off, but otherwise still has to pass
        // its base-status filter below. This lets the user toggle Missing Fees noise
        // without losing visibility of underlying status (Underpayment, etc.).
        if (record.isMissingFees && !this.showMissingFees) return false;

        // O/U records controlled by Over/Under chip (isolation: VALID records with
        // hasOverUnderAdjustment are pulled out of the Valid bucket).
        if (record.validationStatus === 'VALID' && record.hasOverUnderAdjustment) {
          return this.showOverUnder;
        }

        // Status filters
        if (record.validationStatus === 'VALID' && !this.showValid) return false;
        if (record.validationStatus === 'NOT_FOUND' && !this.showNotFound) return false;
        if (record.validationStatus === 'OVERPAYMENT' && !this.showOverpayment) return false;
        if (record.validationStatus === 'UNDERPAYMENT' && !this.showUnderpayment) return false;
        if (record.validationStatus === 'DUPLICATE' && !this.showDuplicate) return false;
        if (record.validationStatus === 'PENDING_EXISTS' && !this.showPendingExists) return false;
        return true;
      });
    },
    summary() {
      const records = this.batchRecords;
      if (!records || records.length === 0) {
        return {
          totalRecords: 0,
          totalAmount: 0,
          validCount: 0,
          validAmount: 0,
          notFoundCount: 0,
          notFoundAmount: 0,
          overpaymentCount: 0,
          overpaymentSum: 0,
          underpaymentCount: 0,
          underpaymentSum: 0,
          duplicateCount: 0,
          duplicateAmount: 0,
          pendingExistsCount: 0,
          pendingExistsAmount: 0,
          overUnderCount: 0,
          overUnderAmount: 0,
          refundAmount: 0,
          paidAmount: 0,
        };
      }

      const totalRecords = records.length;
      const totalAmount = records.reduce((sum, r) => sum + this.getTotalPayment(r), 0);

      const allValidRecords = records.filter((r) => r.validationStatus === 'VALID');
      const overUnderRecords = allValidRecords.filter((r) => r.hasOverUnderAdjustment);
      const overUnderCount = overUnderRecords.length;
      const overUnderAmount = overUnderRecords.reduce(
        (sum, r) => sum + Math.abs(r.amountDifference || 0),
        0,
      );
      const validRecords = allValidRecords.filter((r) => !r.hasOverUnderAdjustment);
      const validCount = validRecords.length;
      const validAmount = validRecords.reduce((sum, r) => sum + this.getTotalPayment(r), 0);

      const notFoundRecords = records.filter((r) => r.validationStatus === 'NOT_FOUND');
      const notFoundCount = notFoundRecords.length;
      const notFoundAmount = notFoundRecords.reduce((sum, r) => sum + this.getTotalPayment(r), 0);

      const overpaymentRecords = records.filter((r) => r.validationStatus === 'OVERPAYMENT');
      const overpaymentCount = overpaymentRecords.length;
      const overpaymentSum = overpaymentRecords.reduce(
        (sum, r) => sum + (r.amountDifference || 0),
        0,
      );

      const underpaymentRecords = records.filter((r) => r.validationStatus === 'UNDERPAYMENT');
      const underpaymentCount = underpaymentRecords.length;
      const underpaymentSum = underpaymentRecords.reduce(
        (sum, r) => sum + Math.abs(r.amountDifference || 0),
        0,
      );

      const duplicateRecords = records.filter((r) => r.validationStatus === 'DUPLICATE');
      const duplicateCount = duplicateRecords.length;
      const duplicateAmount = duplicateRecords.reduce(
        (sum, r) => sum + this.getTotalPayment(r),
        0,
      );

      const pendingExistsRecords = records.filter((r) => r.validationStatus === 'PENDING_EXISTS');
      const pendingExistsCount = pendingExistsRecords.length;
      const pendingExistsAmount = pendingExistsRecords.reduce(
        (sum, r) => sum + this.getTotalPayment(r),
        0,
      );

      // Refund Amount = Not Found Amount + Overpayment Sum + Duplicate Amount + Pending Exists
      const refundAmount = notFoundAmount + overpaymentSum + duplicateAmount + pendingExistsAmount;

      // Paid/Wire Amount = Total Amount Submitted - Refund Amount
      const paidAmount = totalAmount - refundAmount;

      return {
        totalRecords,
        totalAmount,
        validCount,
        validAmount,
        notFoundCount,
        notFoundAmount,
        overpaymentCount,
        overpaymentSum,
        underpaymentCount,
        underpaymentSum,
        duplicateCount,
        duplicateAmount,
        pendingExistsCount,
        pendingExistsAmount,
        overUnderCount,
        overUnderAmount,
        refundAmount,
        paidAmount,
      };
    },
    editNewTotal() {
      return (this.editTaxPayment || 0)
        + (this.editPenaltyPayment || 0)
        + (this.editFeePayment || 0)
        + (this.editOverpaymentAmount || 0);
    },
    editOriginalTotal() {
      if (!this.editingRecord) return 0;
      // If record was previously adjusted, use preserved original values from file
      if (this.editingRecord.isAdjusted
        && this.editingRecord.originalTaxPayment !== undefined) {
        return (this.editingRecord.originalTaxPayment || 0)
          + (this.editingRecord.originalPenaltyPayment || 0)
          + (this.editingRecord.originalFeePayment || 0)
          + (this.editingRecord.originalOverpaymentAmount || 0);
      }
      // Not adjusted yet — use totalPayment (actual bank cash) directly.
      // This is safer than summing tax+penalty+fee+overpay because the import
      // may have reallocated those fields for O/U records.
      return this.editingRecord.totalPayment || 0;
    },
    editTotalMatches() {
      const original = Math.round(this.editOriginalTotal * 100) / 100;
      const newVal = Math.round(this.editNewTotal * 100) / 100;
      return original === newVal;
    },
    // Computed getters/setters for edit fields (string <-> number conversion)
    editTaxPayment: {
      get() {
        return parseFloat(this.editTaxPaymentStr) || 0;
      },
      set(val) {
        const num = parseFloat(val) || 0;
        this.editTaxPaymentStr = num.toFixed(2);
      },
    },
    editPenaltyPayment: {
      get() {
        return parseFloat(this.editPenaltyPaymentStr) || 0;
      },
      set(val) {
        const num = parseFloat(val) || 0;
        this.editPenaltyPaymentStr = num.toFixed(2);
      },
    },
    editFeePayment: {
      get() {
        return parseFloat(this.editFeePaymentStr) || 0;
      },
      set(val) {
        const num = parseFloat(val) || 0;
        this.editFeePaymentStr = num.toFixed(2);
      },
    },
    editOverpaymentAmount: {
      get() {
        return parseFloat(this.editOverpaymentAmountStr) || 0;
      },
      set(val) {
        const num = parseFloat(val) || 0;
        this.editOverpaymentAmountStr = num.toFixed(2);
      },
    },
  },
  created() {
    // Load workstation list
    this.loadPaymentsWkstList();
    // Load batch payment statuses
    this.loadBatchPaymentStatuses();
  },
  beforeDestroy() {
    // Clean up polling interval
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  },
  methods: {
    ...mapMutations('base/crud', ['setItem', 'setIsVisible']),
    ...mapActions('tax/taxPayment', [
      'loadPaymentsWkstList',
      'loadPayment',
      'upsertPayment',
    ]),
    ...mapActions('lockboxImport', [
      'createPaymentFromLockbox',
      'createPaymentFromLockboxAllBatches',
      'updateRecordValidationStatus',
      'updateRecordPayments',
      'approveRecord',
      'rematchRecord',
    ]),
    ...mapActions(['reAuth']),
    getTotalPayment(item) {
      return (item.taxPayment || 0)
        + (item.penaltyPayment || 0)
        + (item.feePayment || 0)
        + (item.overpaymentAmount || 0);
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return '0.00';
      return Math.abs(value).toFixed(2);
    },
    // Preserves the sign — for fields (like Overpayment) where a negative value
    // is semantically distinct from a positive one (shortfall vs. overage).
    formatSignedAmount(value) {
      if (value === null || value === undefined) return '0.00';
      return value.toFixed(2);
    },
    roundToTwoDecimals(value) {
      if (value === null || value === undefined) return 0;
      return Math.round(value * 100) / 100;
    },
    formatPaidDate(value) {
      // Format YYYYMMDD integer to MM/DD/YYYY string
      if (!value) return '';
      const str = String(value);
      if (str.length !== 8) return str;
      const year = str.substring(0, 4);
      const month = str.substring(4, 6);
      const day = str.substring(6, 8);
      return `${month}/${day}/${year}`;
    },
    formatAdjustedDate(value) {
      if (!value) return '';
      const date = new Date(value);
      return date.toLocaleString();
    },
    searchByTaxId(value, search, item) {
      if (!search) return true;
      const term = search.toString().toLowerCase();
      const taxId = (item.taxId || '').toString();
      const taxYear = (item.taxYear || '').toString();
      const parcelId = (item.propertyOrParcelId || '').toString().toLowerCase();
      return taxId.includes(term) || taxYear.includes(term) || parcelId.includes(term);
    },
    statusChipColor(label) {
      const colors = {
        Valid: 'green',
        'Not Found': 'grey',
        Overpayment: 'orange',
        Underpayment: 'red',
        Duplicate: 'purple',
        'Pending Payment': 'blue',
        'Over/Under': 'amber darken-1',
        'Missing Fees': 'brown',
      };
      return colors[label] || 'grey';
    },
    getStatusColor(status) {
      const colors = {
        VALID: 'green',
        OVERPAYMENT: 'orange',
        UNDERPAYMENT: 'red',
        NOT_FOUND: 'grey',
        DUPLICATE: 'purple',
        PENDING_EXISTS: 'blue',
      };
      return colors[status] || 'grey';
    },
    getBatchStatusColor(batch) {
      // Color based on batch status
      if (batch.paymentApplied) return 'success';
      if (batch.validRecords === batch.totalRecords) return 'success';
      if (batch.notFoundRecords > 0) return 'grey';
      return 'primary';
    },
    getDifferenceClass(difference) {
      if (Math.abs(difference) < 0.01) return '';
      if (difference > 0) return 'orange--text';
      return 'red--text';
    },
    isRemovingDuplicate(item) {
      if (this.removingDuplicateIndex === null) {
        return false;
      }
      return item.originalIndex === this.removingDuplicateIndex;
    },
    downloadErrorReport() {
      // Filter only records with errors from current batch
      const errorRecords = this.batchRecords.filter(
        (r) => r.validationStatus !== 'VALID',
      );

      if (errorRecords.length === 0) {
        // eslint-disable-next-line no-alert
        alert('No error records to download');
        return;
      }

      this.downloadCSV(errorRecords, `lockbox-batch-${this.selectedBatch.batchNumber}-errors.csv`);
    },
    async downloadErrorReportPdf() {
      if (!this.importData || !this.importData.fileQueueId) {
        // eslint-disable-next-line no-alert
        alert('No import data available');
        return;
      }

      // Check if there are error records
      const errorRecords = this.batchRecords.filter(
        (r) => r.validationStatus !== 'VALID',
      );

      if (errorRecords.length === 0) {
        // eslint-disable-next-line no-alert
        alert('No error records to download');
        return;
      }

      const { jwt } = await this.reAuth();
      const { backendRest } = clients.direct;
      const params = [
        `fileQueueId=${this.importData.fileQueueId}`,
        `batchNumber=${this.selectedBatch.batchNumber}`,
        `token=${jwt}`,
      ];
      const queryString = generateQueryString(params);
      const url = `${backendRest.defaults.baseURL}/lockbox/printImportErrorList${queryString}`;
      window.open(url, '_blank');
    },
    downloadAllRecords() {
      if (!this.batchRecords || this.batchRecords.length === 0) {
        // eslint-disable-next-line no-alert
        alert('No records to download');
        return;
      }
      this.downloadCSV(this.batchRecords, `lockbox-batch-${this.selectedBatch.batchNumber}-all.csv`);
    },
    downloadCSV(records, filename) {
      // Create CSV content
      const headers = [
        'Tax Year',
        'Tax ID',
        'Property ID',
        'Tax Payment',
        'Expected Tax',
        'Penalty Payment',
        'Expected Penalty',
        'Prev Month Penalty',
        'Fee Payment',
        'Expected Fee',
        'Total Payment',
        'Expected Amount',
        'Prev Month Expected',
        'Paid Date',
        'Difference',
        'Status',
      ];
      const csvRows = [headers.join(',')];

      records.forEach((record) => {
        const row = [
          record.taxYear || '',
          record.taxId || '',
          record.propertyOrParcelId || '',
          record.taxPayment || 0,
          record.expectedTax || 0,
          record.penaltyPayment || 0,
          record.expectedPenalty || 0,
          record.expectedPenaltyPrevMonth || 0,
          record.feePayment || 0,
          record.expectedFee || 0,
          this.getTotalPayment(record),
          record.expectedAmount || 0,
          record.expectedAmountPrevMonth || 0,
          this.formatPaidDate(record.paidDate),
          record.amountDifference || 0,
          record.validationStatus || '',
        ];
        csvRows.push(row.join(','));
      });

      const csvContent = csvRows.join('\n');

      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    downloadFullFileErrorReport() {
      // Filter only records with errors from ALL batches
      if (!this.importData || !this.importData.records) {
        // eslint-disable-next-line no-alert
        alert('No import data available');
        return;
      }

      const errorRecords = this.importData.records.filter(
        (r) => r.validationStatus !== 'VALID',
      );

      if (errorRecords.length === 0) {
        // eslint-disable-next-line no-alert
        alert('No error records to download');
        return;
      }

      this.downloadCSV(errorRecords, 'lockbox-full-file-errors.csv');
    },
    async downloadFullFileErrorReportPdf() {
      if (!this.importData || !this.importData.fileQueueId) {
        // eslint-disable-next-line no-alert
        alert('No import data available');
        return;
      }

      // Check if there are error records
      const errorRecords = this.importData.records.filter(
        (r) => r.validationStatus !== 'VALID',
      );

      if (errorRecords.length === 0) {
        // eslint-disable-next-line no-alert
        alert('No error records to download');
        return;
      }

      const { jwt } = await this.reAuth();
      const { backendRest } = clients.direct;
      const params = [
        `fileQueueId=${this.importData.fileQueueId}`,
        `token=${jwt}`,
      ];
      const queryString = generateQueryString(params);
      const url = `${backendRest.defaults.baseURL}/lockbox/printImportErrorList${queryString}`;
      window.open(url, '_blank');
    },
    downloadFullFileAllRecords() {
      if (!this.importData || !this.importData.records || this.importData.records.length === 0) {
        // eslint-disable-next-line no-alert
        alert('No records to download');
        return;
      }
      this.downloadCSV(this.importData.records, 'lockbox-full-file-all.csv');
    },
    async printAllRecordsPdf() {
      if (!this.importData || !this.importData.fileQueueId) {
        // eslint-disable-next-line no-alert
        alert('No import data available');
        return;
      }

      if (!this.importData.records || this.importData.records.length === 0) {
        // eslint-disable-next-line no-alert
        alert('No records to print');
        return;
      }

      const { jwt } = await this.reAuth();
      const { backendRest } = clients.direct;
      const params = [
        `fileQueueId=${this.importData.fileQueueId}`,
        `token=${jwt}`,
      ];
      const queryString = generateQueryString(params);
      const url = `${backendRest.defaults.baseURL}/lockbox/printAllRecords${queryString}`;
      window.open(url, '_blank');
    },
    async handleCreatePayment() {
      if (!this.importData || !this.importData.fileQueueId || !this.selectedBatch) {
        // eslint-disable-next-line no-alert
        alert('No import data or batch selected');
        return;
      }

      // Check for unapproved overpayment records
      const unapprovedOverpayments = this.batchRecords.filter(
        (r) => r.validationStatus === 'OVERPAYMENT' && !r.isApproved && !r.isAdjusted,
      );

      if (unapprovedOverpayments.length > 0) {
        // eslint-disable-next-line no-alert
        alert(
          `Cannot create payment: ${unapprovedOverpayments.length} overpayment record(s) `
          + 'have not been reviewed.\n\n'
          + 'Please review each overpayment record and either:\n'
          + '- Adjust the payment split and mark as OK, or\n'
          + '- Mark as OK without changes if the overpayment is correct.',
        );
        return;
      }

      this.creatingPayment = true;
      this.statusMessage = 'Creating payment...';

      try {
        // Use entered paidBy or default
        const paidByValue = this.paidBy || this.defaultPaidBy;

        await this.createPaymentFromLockbox({
          fileQueueId: this.importData.fileQueueId,
          batchNumber: this.selectedBatch.batchNumber,
          workstation: this.selectedWorkstation,
          applyOverPayments: this.postOverPayments,
          skipPartials: !this.postPartials,
          skipDuplicates: !this.postDuplicates,
          // Payment type fields
          paymentType: this.selectedPaymentType.toLowerCase(),
          checkNumber: this.selectedPaymentType === 'CHECK' ? this.checkNumber : '',
          eftType: this.selectedPaymentType === 'EFT' ? this.eftType.toLowerCase() : '',
          creditType: this.selectedPaymentType === 'CREDIT' ? this.creditType.toLowerCase() : '',
          paidBy: paidByValue,
        });

        // Start polling for status
        this.statusMessage = 'Payment processing...';
        this.startPolling();
      } catch (error) {
        this.creatingPayment = false;
        this.statusMessage = '';
        // eslint-disable-next-line no-console
        console.error('Error creating payment:', error);
        // eslint-disable-next-line no-alert
        alert(`Error creating payment: ${error.message}`);
      }
    },
    async handleCreatePaymentAll() {
      if (!this.importData || !this.importData.fileQueueId) {
        // eslint-disable-next-line no-alert
        alert('No import data available');
        return;
      }

      // Check for unapproved overpayment records across ALL batches
      if (this.allBatchesUnapprovedOverpaymentCount > 0) {
        // eslint-disable-next-line no-alert
        alert(
          `Cannot create payments: ${this.allBatchesUnapprovedOverpaymentCount} overpayment record(s) `
          + 'across all batches have not been reviewed.\n\n'
          + 'Please review each overpayment record and either:\n'
          + '- Adjust the payment split and mark as OK, or\n'
          + '- Mark as OK without changes if the overpayment is correct.',
        );
        return;
      }

      this.creatingPaymentAll = true;
      this.statusMessage = 'Creating payments for all batches...';

      try {
        const paidByValue = this.paidBy || 'Lockbox Import';

        await this.createPaymentFromLockboxAllBatches({
          fileQueueId: this.importData.fileQueueId,
          workstation: this.selectedWorkstation,
          applyOverPayments: this.postOverPayments,
          skipPartials: !this.postPartials,
          skipDuplicates: !this.postDuplicates,
          paymentType: this.selectedPaymentType.toLowerCase(),
          checkNumber: this.selectedPaymentType === 'CHECK' ? this.checkNumber : '',
          eftType: this.selectedPaymentType === 'EFT' ? this.eftType.toLowerCase() : '',
          creditType: this.selectedPaymentType === 'CREDIT' ? this.creditType.toLowerCase() : '',
          paidBy: paidByValue,
        });

        this.statusMessage = 'Processing all batches...';
        this.startPolling();
      } catch (error) {
        this.creatingPaymentAll = false;
        this.statusMessage = '';
        // eslint-disable-next-line no-console
        console.error('Error creating payments for all batches:', error);
        // eslint-disable-next-line no-alert
        alert(`Error creating payments: ${error.message}`);
      }
    },
    async handleRemoveDuplicate(item) {
      if (!this.importData || !this.importData.fileQueueId || !this.importData.records) {
        // eslint-disable-next-line no-alert
        alert('No import data available');
        return;
      }

      // Use originalIndex which was added when loading data
      const { originalIndex } = item;
      const totalPayment = this.getTotalPayment(item);
      const expectedAmount = item.expectedAmount || 0;

      if (originalIndex === undefined) {
        // eslint-disable-next-line no-alert
        alert('Record index not found');
        return;
      }

      this.removingDuplicateIndex = originalIndex;

      // Determine the appropriate status based on payment comparison
      let newStatus = 'VALID';
      const difference = totalPayment - expectedAmount;
      if (Math.abs(difference) >= 0.01) {
        newStatus = difference > 0 ? 'OVERPAYMENT' : 'UNDERPAYMENT';
      }

      try {
        const result = await this.updateRecordValidationStatus({
          fileQueueId: this.importData.fileQueueId,
          recordIndex: originalIndex,
          validationStatus: newStatus,
        });

        if (result && result.success) {
          // Emit event to parent to refresh data and update the record
          this.$emit('update-record-status', { recordIndex: originalIndex, newStatus });
        } else {
          // eslint-disable-next-line no-alert
          alert(result.message || 'Failed to update record status');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error removing duplicate status:', error);
        // eslint-disable-next-line no-alert
        alert(`Error removing duplicate status: ${error.message}`);
      } finally {
        this.removingDuplicateIndex = null;
      }
    },
    async loadBatchPaymentStatuses() {
      // Load payment status for each batch from FileQueue parameters
      if (this.importData && this.importData.fileQueueId) {
        try {
          const response = await this.$store.dispatch(
            'lockboxImport/getFileQueueStatus',
            {
              fileQueueId: this.importData.fileQueueId,
            },
          );

          if (response && response.Parameters && response.Parameters.batchPayments) {
            this.batchPayments = response.Parameters.batchPayments;
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error loading batch payment statuses:', error);
        }
      }
    },
    async startPolling() {
      // Poll FileQueue status every 2 seconds
      let attempts = 0;
      const maxAttempts = 120; // 4 minutes timeout (longer for all-batches)
      const isAllBatches = this.creatingPaymentAll;

      this.pollingInterval = setInterval(async () => {
        attempts += 1;

        if (attempts > maxAttempts) {
          clearInterval(this.pollingInterval);
          this.creatingPayment = false;
          this.creatingPaymentAll = false;
          this.statusMessage = 'Operation timed out. Please refresh the page.';
          return;
        }

        try {
          // Poll FileQueue status from backend
          const response = await this.$store.dispatch('lockboxImport/getFileQueueStatus', {
            fileQueueId: this.importData.fileQueueId,
          });

          if (response && response.Parameters && response.Parameters.batchPayments) {
            if (isAllBatches) {
              // All-batches mode: check if allBatchesProcessed flag is set
              if (response.Parameters.allBatchesProcessed
                || response.Status === 'Payment Created') {
                clearInterval(this.pollingInterval);
                this.creatingPayment = false;
                this.creatingPaymentAll = false;
                this.statusMessage = '';
                this.batchPayments = response.Parameters.batchPayments;
                this.$emit('refresh');

                const batchCount = Object.keys(response.Parameters.batchPayments).length;
                // eslint-disable-next-line no-alert
                alert(`Payments created successfully for ${batchCount} batch(es)!`);
              } else {
                // Show progress
                const completedCount = Object.values(response.Parameters.batchPayments)
                  .filter((bp) => bp.paymentCreated).length;
                this.statusMessage = `Processing batches... (${completedCount} completed)`;
              }
            } else {
              // Single-batch mode: check specific batch
              const batchNumber = this.selectedBatch
                ? this.selectedBatch.batchNumber : null;
              const batchPayment = batchNumber
                ? response.Parameters.batchPayments[batchNumber] : null;

              if (batchPayment && batchPayment.paymentCreated) {
                clearInterval(this.pollingInterval);
                this.creatingPayment = false;
                this.statusMessage = '';
                this.batchPayments = response.Parameters.batchPayments;
                this.createdPaymentId = batchPayment.paymentId;
                this.createdPaymentNumber = batchPayment.paymentNumber;
                this.createdPaymentFiscalYear = batchPayment.fiscalYear;
                this.$emit('refresh');

                // eslint-disable-next-line no-alert
                alert('Payment created successfully!');
              }
            }
          }

          if (response && response.Status === 'Error') {
            clearInterval(this.pollingInterval);
            this.creatingPayment = false;
            this.creatingPaymentAll = false;
            this.statusMessage = '';

            const errorMsg = response.ErrorDetail && response.ErrorDetail[0]
              ? response.ErrorDetail[0].ErrorDescription
              : 'Unknown error occurred';
            // eslint-disable-next-line no-alert
            alert(`Error: ${errorMsg}`);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error polling FileQueue:', error);
        }
      }, 2000);
    },
    async handleViewPayment() {
      if (!this.createdPaymentNumber || !this.createdPaymentFiscalYear) {
        // eslint-disable-next-line no-alert
        alert('Payment information not available');
        return;
      }

      try {
        // Load the payment using payment number and fiscal year
        const payments = await this.loadPayment({
          baseCrudKey: this.paymentDialogBaseCrudKey,
          criteria: {
            paymentNumber: this.createdPaymentNumber,
            fiscalYear: this.createdPaymentFiscalYear,
          },
        });

        if (payments && payments.length > 0) {
          // Set the payment item in the store
          this.setItem([this.paymentDialogBaseCrudKey, payments[0]]);
          // Open the dialog
          this.setIsVisible([this.paymentDialogBaseCrudKey, true]);
        } else {
          // eslint-disable-next-line no-alert
          alert('Payment not found');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading payment:', error);
        // eslint-disable-next-line no-alert
        alert('Error loading payment details');
      }
    },
    async onSavePayment(shouldPrint = false) {
      try {
        const payment = this.item(this.paymentDialogBaseCrudKey);
        await this.upsertPayment({
          baseCrudKey: this.paymentDialogBaseCrudKey,
          item: payment,
          shouldPrint,
        });
        // Close the dialog after save
        this.setIsVisible([this.paymentDialogBaseCrudKey, false]);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving payment:', error);
        // eslint-disable-next-line no-alert
        alert('Error saving payment');
      }
    },
    // Populate all edit-dialog fields from a record. Called from both
    // openEditDialog (first open) and the importData watcher (on refresh after
    // rematch/auto-refresh) so both entry points use identical logic — in
    // particular, the non-adjusted overpayment derivation below.
    applyRecordToEditFields(record) {
      this.editingRecord = record;
      this.editTaxYear = record.taxYear || null;
      this.editTaxId = record.taxId || null;
      // Format to 2 decimals as strings for proper display
      this.editTaxPaymentStr = (record.taxPayment || 0).toFixed(2);
      this.editPenaltyPaymentStr = (record.penaltyPayment || 0).toFixed(2);
      this.editFeePaymentStr = (record.feePayment || 0).toFixed(2);
      // For non-adjusted records, derive overpayment from the bank's
      // totalPayment so the dialog always shows balanced — even if the stored
      // `overpaymentAmount` field is missing, stale, or zeroed by an older
      // import. For adjusted records, honor the user's saved value.
      if (record.isAdjusted) {
        this.editOverpaymentAmountStr = (record.overpaymentAmount || 0).toFixed(2);
      } else {
        const totalCash = record.totalPayment || 0;
        const currentSum = (record.taxPayment || 0)
          + (record.penaltyPayment || 0)
          + (record.feePayment || 0);
        const derivedOverpayment = Math.round((totalCash - currentSum) * 100) / 100;
        this.editOverpaymentAmountStr = derivedOverpayment.toFixed(2);
      }
      this.editApproved = record.isApproved || false;
    },
    openEditDialog(item) {
      this.applyRecordToEditFields(item);
      this.editingRecordIndex = item.originalIndex;
      this.editDialog = true;
    },
    closeEditDialog() {
      this.editDialog = false;
      this.editingRecord = null;
      this.editingRecordIndex = null;
      this.editApproved = false;
      // Reset rematch fields
      this.editTaxYear = null;
      this.editTaxId = null;
      this.rematching = false;
    },
    recalculateOverpayment() {
      // Auto-calculate overpayment to balance the total
      // overpayment = originalTotal - (tax + penalty + fee)
      if (!this.editingRecord) return;

      // Use editOriginalTotal which handles adjusted vs non-adjusted records
      const originalTotal = this.editOriginalTotal;

      const currentSum = (this.editTaxPayment || 0)
        + (this.editPenaltyPayment || 0)
        + (this.editFeePayment || 0);

      // Calculate overpayment needed to balance, rounded to 2 decimals.
      // Allow negative values (underpayment adjustments from O/U).
      const newOverpayment = Math.round((originalTotal - currentSum) * 100) / 100;
      this.editOverpaymentAmountStr = newOverpayment.toFixed(2);
    },
    async saveEditPayments() {
      if (!this.editTotalMatches) {
        // eslint-disable-next-line no-alert
        alert('Payment amounts must equal the original total');
        return;
      }

      if (!this.importData || !this.importData.fileQueueId) {
        // eslint-disable-next-line no-alert
        alert('No import data available');
        return;
      }

      this.savingEdit = true;

      try {
        // Update payment amounts
        const result = await this.updateRecordPayments({
          fileQueueId: this.importData.fileQueueId,
          recordIndex: this.editingRecordIndex,
          taxPayment: this.editTaxPayment,
          penaltyPayment: this.editPenaltyPayment,
          feePayment: this.editFeePayment,
          overpaymentAmount: this.editOverpaymentAmount,
        });

        if (!result || !result.success) {
          // eslint-disable-next-line no-alert
          alert(result.message || 'Failed to update payment amounts');
          return;
        }

        // Update approval status if changed
        const approvalChanged = this.editApproved !== (this.editingRecord.isApproved || false);
        if (approvalChanged) {
          await this.approveRecord({
            fileQueueId: this.importData.fileQueueId,
            recordIndex: this.editingRecordIndex,
            isApproved: this.editApproved,
          });
        }

        this.closeEditDialog();
        // Emit event to parent to refresh data
        this.$emit('refresh');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating payment amounts:', error);
        // eslint-disable-next-line no-alert
        alert(`Error updating payment amounts: ${error.message}`);
      } finally {
        this.savingEdit = false;
      }
    },
    async handleRematch() {
      if (!this.importData || !this.importData.fileQueueId || !this.editingRecord) {
        // eslint-disable-next-line no-alert
        alert('No import data or record selected');
        return;
      }

      if (!this.editTaxYear || !this.editTaxId) {
        // eslint-disable-next-line no-alert
        alert('Please enter both Tax Year and Tax ID');
        return;
      }

      this.rematching = true;
      try {
        const result = await this.rematchRecord({
          fileQueueId: this.importData.fileQueueId,
          recordIndex: this.editingRecordIndex,
          taxYear: this.editTaxYear,
          taxId: this.editTaxId,
        });

        if (result.success) {
          // Keep modal open and refresh data - the watcher will update editingRecord
          this.$emit('refresh');
        } else {
          // eslint-disable-next-line no-alert
          alert(result.message || 'Rematch failed');
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error rematching record:', error);
        // eslint-disable-next-line no-alert
        alert(`Error rematching record: ${error.message}`);
      } finally {
        this.rematching = false;
      }
    },
  },
  watch: {
    importData: {
      immediate: true,
      handler() {
        this.loadBatchPaymentStatuses();
        // Auto-fill paidBy when import data loads
        if (this.importData && this.selectedBatch && !this.paidBy) {
          this.paidBy = this.defaultPaidBy;
        }
        // Update editingRecord if edit dialog is open (for rematch refresh)
        if (this.editDialog && this.editingRecordIndex !== null && this.importData
          && this.importData.records) {
          const updatedRecord = this.importData.records.find(
            (r) => r.originalIndex === this.editingRecordIndex,
          );
          if (updatedRecord) {
            // Reuse the same logic as first-open so the non-adjusted
            // overpayment derivation applies on refresh too.
            this.applyRecordToEditFields(updatedRecord);
          }
        }
      },
    },
    selectedBatchIndex() {
      // Reset payment status tracking when switching batches
      if (this.selectedBatch && this.batchPayments[this.selectedBatch.batchNumber]) {
        const batchPayment = this.batchPayments[this.selectedBatch.batchNumber];
        this.createdPaymentId = batchPayment.paymentId || null;
        this.createdPaymentNumber = batchPayment.paymentNumber || null;
        this.createdPaymentFiscalYear = batchPayment.fiscalYear || null;
      } else {
        this.createdPaymentId = null;
        this.createdPaymentNumber = null;
        this.createdPaymentFiscalYear = null;
      }
      // Auto-fill paidBy field with new batch's default
      this.paidBy = this.defaultPaidBy;
    },
    // Auto-calculate overpayment when top 3 fields change
    editTaxPaymentStr() {
      this.recalculateOverpayment();
    },
    editPenaltyPaymentStr() {
      this.recalculateOverpayment();
    },
    editFeePaymentStr() {
      this.recalculateOverpayment();
    },
  },
};
</script>

<style scoped>
.summary-panel {
  min-width: 180px;
  max-width: 220px;
  flex-shrink: 0;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 2px;
}
.summary-label {
  font-size: 0.75rem;
}
.summary-value {
  font-size: 0.75rem;
  font-weight: 500;
}
</style>
